import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMembership } from '../contexts/MembershipContext';
import authService from '../services/authService';

interface UserProfile {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  updated_at: string;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const {
    membershipInfo,
    usageHistory,
    loading: membershipLoading,
    getTierName,
  } = useMembership();
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    // Fetch profile data once user is authenticated
    if (user && !loading) {
      fetchProfileData();
    }
  }, [loading, user, navigate]);

  const fetchProfileData = async () => {
    try {
      setProfileLoading(true);
      setError('');

      const userData = await authService.getProfile();
      setProfileData(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
      console.error('Profile fetch error:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSupport = () => {
    const userTier = membershipInfo
      ? getTierName(membershipInfo.tier)
      : getTierName(1); // Free account
    const isPriority = membershipInfo?.tier !== 1;
    const subject = isPriority
      ? '[PRIORITY] Support Request'
      : 'Support Request';

    const body = encodeURIComponent(`Hello,

User Information:
- Name: ${profileData?.first_name} ${profileData?.last_name}
- Email: ${profileData?.email}
- Tier: ${userTier}

I need help with...`);

    window.location.href = `mailto:they2jstudio@gmail.com?subject=${encodeURIComponent(
      subject
    )}&body=${body}`;
  };

  if (loading || profileLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-gray-50'>
        <div className='text-gray-600'>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  if (error) {
    return (
      <div className='min-h-screen bg-gray-50 py-12 px-4'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-6 text-center'>
            <h1 className='text-2xl font-bold text-red-900 mb-2'>Error</h1>
            <p className='text-red-700'>{error}</p>
            <button
              onClick={fetchProfileData}
              className='mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors'
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData || !profileData.email) {
    return null;
  }

  const joinDate = profileData.created_at
    ? new Date(profileData.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Unknown';

  return (
    <div className='overflow-y-auto h-[100vh] bg-gray-50 py-12 px-4'>
      <div className='max-w-4xl mx-auto mb-12'>
        {/* Profile Header */}
        <div className='bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 mb-4 sm:mb-8'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6'>
            <div className='flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 flex-1'>
              {/* Avatar */}
              <div className='w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0'>
                <span className='text-xl sm:text-2xl md:text-3xl font-bold text-blue-600'>
                  {profileData.first_name?.charAt(0).toUpperCase() || 'U'}
                  {profileData.last_name?.charAt(0).toUpperCase() || 'N'}
                </span>
              </div>

              {/* User Info */}
              <div className='flex-1 text-center sm:text-left'>
                <h1 className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2'>
                  {profileData.first_name || 'Unknown'}{' '}
                  {profileData.last_name || 'User'}
                </h1>
                <p className='text-sm sm:text-base text-gray-500 mb-2'>{profileData.email}</p>
                <p className='text-xs sm:text-sm text-gray-500 mb-3 sm:mb-0'>
                  Member since: {joinDate}
                </p>
                
                {/* Get Support Button - Mobile */}
                <div className='sm:hidden'>
                  <button
                    onClick={handleSupport}
                    className='border border-gray-200 text-blue-600 hover:bg-blue-50 font-medium py-2 px-4 rounded-lg transition-colors text-sm w-full'
                  >
                    Get Support
                  </button>
                </div>
              </div>
            </div>

            {/* Get Support Button - Desktop */}
            <div className='hidden sm:block'>
              <button
                onClick={handleSupport}
                className='border border-gray-200 text-blue-600 hover:bg-blue-50 font-medium py-2 px-6 rounded-lg transition-colors'
              >
                Get Support
              </button>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className='grid md:grid-cols-1 gap-4 sm:gap-8'>
          {/* Membership Info */}
          <div className='bg-white rounded-lg shadow-md p-4 sm:p-6'>
            <h2 className='text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6'>
              Usage overview
            </h2>
            {membershipLoading ? (
              <div className='text-center text-gray-600'>
                Loading membership data...
              </div>
            ) : membershipInfo ? (
              <div className='space-y-4 sm:space-y-6'>
                {/* Billing Cycle */}
                <div className='text-xs sm:text-sm text-gray-600'>
                  Usage renews on{' '}
                  {(() => {
                    const endDate = new Date(
                      membershipInfo.billing_period.end_date
                        .replace('Z', '')
                        .replace(/[+-]\d{2}:\d{2}$/, '')
                    );
                    const renewalDate = new Date(endDate.getTime() + 60 * 1000); // Add 1 minute
                    return renewalDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    });
                  })()}
                </div>

                {/* Current Tier */}
                <div className='mb-3 sm:mb-4'>
                  <div className='text-base sm:text-lg font-medium text-gray-900 mb-2'>
                    {getTierName(membershipInfo.tier)}
                  </div>
                </div>

                {/* Usage Bar */}
                <div className='space-y-2 sm:space-y-3'>
                  <div className='flex justify-between items-center flex-wrap gap-1 sm:gap-0'>
                    <span className='text-xs sm:text-sm font-medium text-gray-700'>
                      Minutes used this month
                    </span>
                    <span className='text-xs sm:text-sm text-gray-600 whitespace-nowrap'>
                      {membershipInfo.used_minutes} /{' '}
                      {membershipInfo.monthly_minutes} min
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className='w-full bg-gray-200 rounded-full h-3'>
                    <div
                      className='bg-blue-600 h-3 rounded-full transition-all duration-500'
                      style={{
                        width: `${Math.min(
                          (membershipInfo.used_minutes /
                            membershipInfo.monthly_minutes) *
                            100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>

                  <div className='text-xs sm:text-sm text-gray-600'>
                    {membershipInfo.remaining_minutes} minutes remaining
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='flex gap-2 sm:gap-3 pt-3 sm:pt-4'>
                  <button
                    onClick={() => navigate(`/profile/subscription`)}
                    className='text-blue-600 hover:text-gray-700 hover:bg-gray-50 font-medium py-2 px-3 sm:px-4 rounded-lg transition-colors border border-gray-200 hover:border-gray-300 text-sm sm:text-base'
                  >
                    Manage Subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className='text-center text-gray-500'>
                Unable to load membership data
              </div>
            )}
          </div>
        </div>

        {/* Usage History */}
        <div className='bg-white rounded-lg shadow-md p-4 sm:p-6 mt-4 sm:mt-8'>
          <h2 className='text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6'>
            Usage History
          </h2>
          {membershipLoading ? (
            <div className='text-center text-gray-600'>
              Loading usage history...
            </div>
          ) : usageHistory && usageHistory.usage_history.length > 0 ? (
            <div className='space-y-3 sm:space-y-4'>
              <div className='text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4'>
                Total records: {usageHistory.total_records}
              </div>
              <div className='space-y-2 max-h-48 sm:max-h-64 overflow-y-auto'>
                {usageHistory.usage_history.map((record, index) => (
                  <div
                    key={record.id || `usage-record-${index}`}
                    className='border border-gray-200 rounded-lg p-2 sm:p-3'
                  >
                    <div className='flex justify-between items-start sm:items-center flex-col sm:flex-row gap-2 sm:gap-0'>
                      <div className='flex-1'>
                        <div className='font-medium text-gray-900 text-sm sm:text-base'>
                          {Math.round(record.duration_seconds / 60)} minutes
                        </div>
                        <div className='text-xs sm:text-sm text-gray-600'>
                          Feature: {record.feature_used}
                        </div>
                      </div>
                      <div className='text-xs sm:text-sm text-gray-500 whitespace-nowrap self-end sm:self-auto'>
                        {new Date(record.created_at).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className='text-center text-gray-500'>
              No usage history found
            </div>
          )}
        </div>

        {/* Settings Section */}
        <div className='p-4 sm:p-6 mt-4 sm:mt-8'>
          <div className='flex justify-center'>
            <button
              onClick={handleLogout}
              className='bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base w-full sm:w-auto max-w-xs'
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
